/**
 * SignatureRequestModal Component
 * Single Responsibility: Handle signature request options (digital, email, manual)
 */

import React, { useState } from 'react'
import { X, Check, AlertCircle } from 'lucide-react'
import { ComplianceDocument } from '../../../types/meeting'
import { SignatureField } from '../../forms/fields/SignatureField'
import { toast } from 'sonner'

interface SignatureRequestModalProps {
  isOpen: boolean
  onClose: () => void
  document: ComplianceDocument | null
  method: 'digital' | 'email' | 'paper'
  onSignatureComplete: (signature: string, method: 'digital' | 'email' | 'paper') => void
}

const SignatureRequestModal: React.FC<SignatureRequestModalProps> = ({
  isOpen,
  onClose,
  document,
  method,
  onSignatureComplete
}) => {
  const [signature, setSignature] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [paperDetails, setPaperDetails] = useState({
    signerName: '',
    signedDate: new Date().toISOString().split('T')[0],
    verificationNotes: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen || !document) return null

  const handleDigitalSignatureSubmit = () => {
    if (!signature) {
      toast.error('Please provide a signature')
      return
    }
    setIsSubmitting(true)
    setTimeout(() => {
      onSignatureComplete(signature, 'digital')
      toast.success('Signature captured successfully')
      setIsSubmitting(false)
      onClose()
    }, 1000)
  }

  const handleEmailSubmit = () => {
    if (!email) {
      toast.error('Please enter an email address')
      return
    }
    setIsSubmitting(true)
    setTimeout(() => {
      onSignatureComplete(email, 'email')
      toast.success(`Signature request sent to ${email}`)
      setIsSubmitting(false)
      onClose()
    }, 1500)
  }

  const handlePaperSubmit = () => {
    if (!paperDetails.signerName) {
      toast.error('Please enter the signer name')
      return
    }
    setIsSubmitting(true)
    setTimeout(() => {
      onSignatureComplete(JSON.stringify(paperDetails), 'paper')
      toast.success('Paper signature recorded successfully')
      setIsSubmitting(false)
      onClose()
    }, 1000)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              {method === 'digital' && '✍️ Digital Signature Request'}
              {method === 'email' && '📧 Email Signature Request'}
              {method === 'paper' && '📝 Manual Signature Verification'}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Document: {document.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Digital Signature */}
          {method === 'digital' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800">
                      Please sign below to confirm you have reviewed and agree to the contents of this document.
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      This digital signature is legally binding.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <SignatureField
                  name="signature"
                  label="Your Signature"
                  value={signature}
                  onChange={(value) => setSignature(value)}
                  onClear={() => setSignature(null)}
                  required
                  width={500}
                  height={150}
                />
              </div>

              <div className="text-sm text-slate-600">
                <p>By signing this document, you acknowledge that:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>You have read and understood the document</li>
                  <li>You agree to the terms and conditions</li>
                  <li>This signature is legally binding</li>
                </ul>
              </div>
            </div>
          )}

          {/* Email Request */}
          {method === 'email' && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-sm text-slate-700">
                  An email will be sent to the recipient with a secure link to sign the document electronically.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Recipient Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-800">
                      The recipient will have 7 days to sign the document.
                    </p>
                    <p className="text-xs text-yellow-600 mt-1">
                      You will be notified once the document is signed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-sm text-slate-600">
                <p className="font-medium mb-2">Email will include:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Document preview</li>
                  <li>Secure signing link</li>
                  <li>Instructions for electronic signature</li>
                  <li>Contact information for questions</li>
                </ul>
              </div>
            </div>
          )}

          {/* Manual/Paper Verification */}
          {method === 'paper' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  Record details of a paper signature that was obtained in person.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Signer's Full Name *
                </label>
                <input
                  type="text"
                  value={paperDetails.signerName}
                  onChange={(e) => setPaperDetails({...paperDetails, signerName: e.target.value})}
                  placeholder="Signer full name"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Date Signed *
                </label>
                <input
                  type="date"
                  value={paperDetails.signedDate}
                  onChange={(e) => setPaperDetails({...paperDetails, signedDate: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Verification Notes
                </label>
                <textarea
                  value={paperDetails.verificationNotes}
                  onChange={(e) => setPaperDetails({...paperDetails, verificationNotes: e.target.value})}
                  placeholder="e.g., Signed in person during meeting, original filed in client folder"
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800">
                      I confirm that I have witnessed the paper signature and verified the signer's identity.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={
              method === 'digital' ? handleDigitalSignatureSubmit :
              method === 'email' ? handleEmailSubmit :
              handlePaperSubmit
            }
            disabled={isSubmitting}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Processing...
              </span>
            ) : (
              <>
                {method === 'digital' && 'Submit Signature'}
                {method === 'email' && 'Send Request'}
                {method === 'paper' && 'Record Signature'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SignatureRequestModal